
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ICONS } from '../constants';
import { createStripeCheckoutSession, createCustomerPortalSession, auth, getUserDocument, STRIPE_PRICES, redeemPromoCode } from '../services/firebase';
import clsx from 'clsx';

interface SubscriptionModalProps {
  onClose: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ onClose }) => {
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('trial');
  
  // Promo Code State
  const [promoCode, setPromoCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
      const fetchPlan = async () => {
          if (auth.currentUser) {
              const user = await getUserDocument(auth.currentUser.uid);
              if (user && user.plan) setCurrentPlan(user.plan);
          }
      };
      fetchPlan();
  }, []);

  // PLANS CONFIGURATION
  const plans = [
    {
      name: 'TRIAL',
      price: 'Free',
      priceId: null,
      period: '/mo',
      features: [
          '5 RESHOOTS / month', 
          '1K Resolution',
          'Standard License'
      ],
      recommended: false,
      buttonText: 'Current Plan',
      planId: 'trial',
      color: 'border-amber-700/50',
      bg: 'bg-amber-900/10',
      disabled: false
    },
    {
      name: 'Creator',
      price: 'BETA PRICE: 17.99',
      priceId: 'creator_link',
      period: '/mo',
      features: [
          '50 RESHOOTS / month',
          '2K Resolution',
          'Commercial License'
      ],
      recommended: false,
      buttonText: 'Upgrade to Creator',
      planId: 'creator',
      color: 'border-zinc-400',
      bg: 'bg-zinc-800/40',
      disabled: false
    },
    {
      name: 'Pro',
      price: 'BETA PRICE: 37.99',
      priceId: 'pro_link',
      period: '/mo',
      features: [
          '125 RESHOOTS / month', 
          '2K Resolution',
          'Commercial License'
      ],
      recommended: true,
      buttonText: 'Upgrade to Pro',
      planId: 'pro',
      color: 'border-yellow-500',
      bg: 'bg-yellow-900/10',
      disabled: false
    }
  ];

  const handleSubscribe = async (priceId: string | null) => {
      if (!priceId) return;
      
      // Direct Link Redirects for Beta Prices
      if (priceId === 'creator_link') {
          window.location.assign("https://buy.stripe.com/aFaeVf0t1ajp4ms65y1Jm06");
          return;
      }
      if (priceId === 'pro_link') {
          window.location.assign("https://buy.stripe.com/8x2cN78ZxgHNg5aalO1Jm07");
          return;
      }

      setLoadingPriceId(priceId);
      setErrorMsg(null);

      try {
          const url = await createStripeCheckoutSession(priceId);
          window.location.assign(url);
      } catch (error: any) {
          console.error("[UI] Subscription Error:", error);
          setErrorMsg(error.message || "Failed to initiate checkout.");
          setLoadingPriceId(null);
      }
  };

  const handleManageSubscription = async () => {
      setLoadingPriceId('portal');
      setErrorMsg(null);
      try {
          const url = await createCustomerPortalSession();
          window.location.assign(url);
      } catch (error: any) {
          setErrorMsg(error.message);
          setLoadingPriceId(null);
      }
  };

  const handleRedeemCode = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!promoCode.trim()) return;
      if (!auth.currentUser) return;

      setRedeeming(true);
      setPromoMessage(null);

      try {
          const value = await redeemPromoCode(auth.currentUser.uid, promoCode);
          setPromoMessage({ type: 'success', text: `Successfully added ${value} RESHOOTS!` });
          setPromoCode('');
      } catch (error: any) {
          setPromoMessage({ type: 'error', text: error.message || "Invalid or expired code." });
      } finally {
          setRedeeming(false);
      }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[130] bg-black/90 backdrop-blur-xl flex items-end md:items-center justify-center p-0 md:p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-title"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="w-full max-w-5xl bg-zinc-950 border border-white/10 rounded-t-2xl md:rounded-2xl overflow-hidden shadow-2xl relative flex flex-col max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <div className="absolute top-4 right-4 z-20">
             <button
                onClick={onClose}
                className="p-2 bg-black/50 hover:bg-white/10 rounded-full text-zinc-400 hover:text-white transition-colors backdrop-blur-md border border-white/5 focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="Close Subscription Modal"
            >
                <ICONS.Close className="w-5 h-5" />
            </button>
          </div>

          <div className="overflow-y-auto p-6 md:p-10 scrollbar-hide">
             <div className="text-center mb-10">
                <h2 id="subscription-title" className="text-3xl md:text-4xl font-light text-white mb-3 tracking-tight">Upgrade Your Studio</h2>
                <p className="text-sm text-zinc-400 max-w-lg mx-auto">
                    Unlock higher capacity, professional resolution, and commercial rights. All plans include full access to the AI transformation engine.
                </p>
                {errorMsg && (
                    <div role="alert" className="mt-4 p-3 bg-red-900/20 border border-red-900/50 rounded text-red-400 text-xs font-mono">
                        {errorMsg}
                    </div>
                )}
             </div>
             
             {/* Portal Button for existing subscribers */}
             {currentPlan !== 'trial' && currentPlan !== 'free' && currentPlan !== 'bronze' && (
                 <div className="flex justify-center mb-8">
                     <button 
                        onClick={handleManageSubscription}
                        disabled={loadingPriceId === 'portal'}
                        className="flex items-center gap-2 px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-full text-xs uppercase font-bold tracking-widest transition-all border border-zinc-700 hover:border-zinc-500 focus:outline-none focus:ring-2 focus:ring-white"
                     >
                         {loadingPriceId === 'portal' ? (
                             <ICONS.Refresh className="w-4 h-4 animate-spin" />
                         ) : (
                             <ICONS.Settings className="w-4 h-4" />
                         )}
                         Manage Subscription / Cancel
                     </button>
                 </div>
             )}
             
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => {
                  // Normalize current plan check
                  const normalizedCurrent = (currentPlan === 'free' || currentPlan === 'bronze') ? 'trial' : (currentPlan === 'silver' ? 'creator' : (currentPlan === 'gold' ? 'pro' : currentPlan));
                  const isCurrent = normalizedCurrent === plan.planId;
                  
                  return (
                    <div 
                        key={plan.name}
                        className={clsx(
                        "relative p-6 rounded-xl border flex flex-col items-center transition-all duration-300",
                        plan.bg,
                        plan.color,
                        (plan.recommended && !isCurrent) ? "shadow-[0_0_30px_rgba(255,255,255,0.05)] border-white/40 scale-105 z-10" : "border-white/5 hover:border-white/20"
                        )}
                        role="article"
                    >
                        {plan.recommended && !isCurrent && (
                        <div className="absolute -top-3 px-3 py-1 bg-white text-black text-[10px] font-bold uppercase tracking-widest rounded-full shadow-lg">
                            Recommended
                        </div>
                        )}
                        
                        <h3 className="text-lg font-medium text-white mb-2 tracking-wide uppercase">{plan.name}</h3>
                        <div className="flex items-baseline mb-6 text-center">
                            <span className={clsx(
                                "font-light text-white",
                                plan.price.includes('BETA') ? "text-xl" : "text-3xl"
                            )}>{plan.price}</span>
                            {plan.price !== 'Free' && <span className="text-zinc-500 text-xs ml-1">{plan.period}</span>}
                        </div>
                        
                        <div className="w-full border-t border-white/5 mb-6"></div>

                        <ul className="space-y-3 mb-8 w-full text-left flex-1">
                        {plan.features.map((feat, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-zinc-300">
                            <ICONS.Check className="w-3.5 h-3.5 text-white shrink-0 mt-0.5" aria-hidden="true" />
                            <span className="leading-snug">{feat}</span>
                            </li>
                        ))}
                        </ul>
                        
                        <button
                        onClick={() => handleSubscribe(plan.priceId)}
                        disabled={isCurrent || plan.disabled || (loadingPriceId !== null && !plan.priceId?.includes('_link'))}
                        className={clsx(
                            "w-full py-3 rounded-lg font-bold text-[10px] uppercase tracking-[0.15em] transition-all mt-auto flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-white",
                            (plan.recommended && !isCurrent) ? "bg-white text-black hover:bg-zinc-200" : "bg-white/5 text-white hover:bg-white/10 border border-white/10",
                            (isCurrent || plan.disabled) && "opacity-50 cursor-default bg-transparent border border-zinc-800 text-zinc-500 hover:bg-transparent hover:text-zinc-500",
                            (loadingPriceId !== null && !isCurrent && !plan.priceId?.includes('_link')) && "opacity-50 cursor-wait"
                        )}
                        >
                        {loadingPriceId !== null && loadingPriceId === plan.priceId ? (
                            <>
                                <ICONS.Refresh className="w-3 h-3 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                {isCurrent ? <><ICONS.Check className="w-3 h-3" /> Current Plan</> : plan.buttonText}
                            </>
                        )}
                        </button>
                    </div>
                  );
                })}
             </div>
             
             {/* PROMO CODE SECTION */}
             <div className="mt-12 max-w-lg mx-auto bg-zinc-900/50 rounded-xl p-6 border border-white/5">
                <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-4 text-center">Have a Promo Code?</h3>
                <form onSubmit={handleRedeemCode} className="flex gap-2">
                    <input 
                        type="text" 
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        placeholder="ENTER CODE"
                        className="flex-1 bg-black/50 border border-zinc-700 rounded px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 uppercase"
                    />
                    <button 
                        type="submit"
                        disabled={redeeming || !promoCode}
                        className="bg-white text-black px-4 py-2 rounded text-xs font-bold uppercase tracking-widest hover:bg-zinc-200 disabled:opacity-50"
                    >
                        {redeeming ? '...' : 'Redeem'}
                    </button>
                </form>
                {promoMessage && (
                    <div className={clsx(
                        "mt-3 text-xs text-center font-mono p-2 rounded",
                        promoMessage.type === 'success' ? "text-green-400 bg-green-900/10 border border-green-900/20" : "text-red-400 bg-red-900/10 border border-red-900/20"
                    )}>
                        {promoMessage.text}
                    </div>
                )}
             </div>

             <p className="mt-8 text-[10px] text-zinc-600 text-center max-w-xl mx-auto">
                 RESHOOTS reset monthly based on your billing date. Unused RESHOOTS do not roll over. 
                 Secure payment processing by Stripe. Cancel anytime.
             </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SubscriptionModal;
